import React from "react";
import { motion } from "framer-motion";
import { Search, MessageCircle, ShoppingCart, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const HowBuyersFindPartsSection = ({ variants }) => {
  const sectionAnimation = variants || {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const steps = [
    {
      icon: <Search className="h-10 w-10 text-primary mb-4" />,
      title: "1. Find Your Part Effortlessly",
      description:
        "Use our smart AI assistant, Kato, or browse through intuitive categories. Finding the exact auto part you need is quick and simple.",
    },
    {
      icon: <MessageCircle className="h-10 w-10 text-primary mb-4" />,
      title: "2. Connect Directly with Sellers",
      description:
        "Once you find your part, message sellers directly. Ask questions, clarify details, and get all the information you need instantly.",
    },
    {
      icon: <ShoppingCart className="h-10 w-10 text-primary mb-4" />,
      title: "3. Arrange Your Purchase",
      description:
        "Easily coordinate payment and pickup or delivery with the seller. Secure your part hassle-free and get your vehicle back on the road.",
    },
  ];

  return (
    <motion.section
      className="py-16 md:py-24 bg-background"
      variants={sectionAnimation}
      initial="hidden"
      animate="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="container mx-auto px-4">
        <motion.div
          variants={itemAnimation}
          className="text-center mb-12 md:mb-16"
        >
          <CheckCircle className="h-12 w-12 text-accent mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Finding Parts is{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              Simple & Swift
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
            We've streamlined the process so you can get from search to solution
            in no time. Here’s how easy it is for buyers:
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10"
          variants={sectionAnimation}
        >
          {steps.map((step, index) => (
            <motion.div key={index} variants={itemAnimation}>
              <Card className="h-full text-center p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-primary bg-gradient-to-br from-card to-secondary/10">
                <CardHeader className="p-0 mb-4 flex flex-col items-center">
                  {step.icon}
                  <CardTitle className="text-xl md:text-2xl font-semibold text-foreground">
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <p className="text-muted-foreground text-sm md:text-base">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HowBuyersFindPartsSection;
